import { useEffect, useState } from 'react';
import { VyDialog } from '../../../../../../../../../../../components/new-design/feedback/VyDialog';
import { Autocomplete, Stack } from '@mui/material';
import { TextField } from '@mui/material';
import { INTEGRATION_PROVIDERS, QuickBooksProduct } from '@vy/types';
import { useModifyExternalProduct } from '../../../../../../../../../../../hooks/requests/integrations';
import { runMutation } from '../../../../../../../../../../../utils/vy-mutation';
import { fromNullToUndefined } from '@vy/utils';
import { useQueryClient } from 'react-query';

type EditQuickbooksProductDialogProps = {
  open: boolean;
  onClose: () => void;
  productId: string;
  externalProductId: string | undefined;
  quickBooksProducts: QuickBooksProduct[];
};

export const EditQuickbooksProductDialog = ({
  open,
  onClose,
  productId,
  externalProductId,
  quickBooksProducts,
}: EditQuickbooksProductDialogProps) => {
  const modifyExternalProductMutation = useModifyExternalProduct(productId);
  const queryClient = useQueryClient();

  const [selectedQuickBooksProductId, setSelectedQuickBooksProductId] = useState<string | null>(
    externalProductId ?? null,
  );

  useEffect(() => {
    if (open) {
      setSelectedQuickBooksProductId(externalProductId ?? null);
    }
  }, [open, externalProductId]);

  const selectedQuickBooksProduct = quickBooksProducts.find(
    (product) => product.id === selectedQuickBooksProductId,
  );

  const handleSubmit = async () => {
    await runMutation(
      modifyExternalProductMutation,
      {
        externalId: fromNullToUndefined(selectedQuickBooksProductId),
        integration: INTEGRATION_PROVIDERS.quickBooks,
        productId,
      },
      {
        pending: 'Updating product...',
        success: 'Product updated successfully',
        error: 'Failed to update product',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            predicate: (query) =>
              Array.isArray(query.queryKey) &&
              query.queryKey[0] === 'invoice-products-integrations',
          });
          onClose();
        },
      },
    );
  };

  return (
    <VyDialog
      open={open}
      onClose={onClose}
      label="Edit QB Product"
      cancelButtonText="Cancel"
      ctaText="Save"
      onSubmit={handleSubmit}
    >
      <Stack>
        <Autocomplete
          options={quickBooksProducts ?? []}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedQuickBooksProduct}
          onChange={(event, value) => setSelectedQuickBooksProductId(value?.id ?? null)}
          renderInput={(params) => <TextField {...params} label="QB ERP ID" />}
        />
      </Stack>
    </VyDialog>
  );
};
